import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-ink-inv hover:bg-primary-hover active:bg-primary-press focus-visible:shadow-focus',
  secondary:
    'bg-paper-0 text-ink-0 border border-line-2 hover:bg-paper-1 focus-visible:shadow-focus',
  ghost: 'bg-transparent text-ink-1 hover:bg-paper-2 focus-visible:shadow-focus',
  destructive: 'bg-status-err text-ink-inv hover:brightness-95 focus-visible:shadow-focus',
};

const SIZE_TEXT: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-meta rounded-token-sm',
  md: 'h-9 px-3.5 text-ui rounded-token-md',
  lg: 'h-11 px-4 text-body rounded-token-lg',
};

const SIZE_ICON_ONLY: Record<ButtonSize, string> = {
  sm: 'h-7 w-7 rounded-token-sm',
  md: 'h-9 w-9 rounded-token-md',
  lg: 'h-11 w-11 rounded-token-lg',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconOnly?: boolean;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    iconOnly = false,
    loading = false,
    leadingIcon,
    trailingIcon,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 font-medium outline-none transition-colors duration-2 ease-brand',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        iconOnly ? SIZE_ICON_ONLY[size] : SIZE_TEXT[size],
        VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : leadingIcon}
      {iconOnly ? null : children}
      {!loading && trailingIcon ? trailingIcon : null}
    </button>
  );
});

function Spinner() {
  return (
    <svg aria-hidden="true" className="h-4 w-4 animate-spin" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path d="M14 8a6 6 0 0 1-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
